// God → Merchants: onboard merchants, browse every user account, and manage one merchant
// (status, redirect URLs, UPI rotation, transactions). "merchants" = list, "merchants/<id>" = detail.
import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft, Banknote, Landmark, Pencil, Plus, Receipt, Search, Settings2, Store, Trash2, UserPlus, Users, UserX,
} from "lucide-react";
import { toast } from "sonner";
import { god } from "../godApi";
import { useDebounced, usePagedList, useSyncFilter } from "../../components/sc/ui";
import { useClientPage } from "../../components/sc/Pager";
import {
  Avatar, Button, Callout, Cell, CopyBtn, Dialog, Empty, Field, Grid, Input, Kpi, Page, Pagination, Pill, Rows,
  SearchInput, Section, Skeleton, StatusPill, Table, Toggle, Toolbar, ago, inr, inrCompact, shortId, useNav, when,
} from "../kit";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VPA_RX = /^[^\s@]+@[^\s@]+$/;
const digits = (s) => s.replace(/[^0-9]/g, "");
const pagesOf = (d, size) => d.pages || Math.max(1, Math.ceil((d.total || 0) / size));

export default function Merchants() {
  const { path } = useNav();
  const id = path.startsWith("merchants/") ? decodeURIComponent(path.slice("merchants/".length)) : "";
  return id ? <MerchantDetail key={id} id={id} /> : <MerchantList />;
}

// ─────────────────────────── list ───────────────────────────
function MerchantList() {
  const { go } = useNav();
  const list = usePagedList((p) => god.users(p), { q: "" }, 25);
  const [qInput, setQInput] = useState("");
  useSyncFilter(list.setFilter, "q", useDebounced(qInput.trim(), 300));
  const [ov, setOv] = useState(null);
  const [confirm, setConfirm] = useState(null); // merchant {id,name,suspended}
  const { load } = list;

  const loadOv = useCallback(() => { god.overview().then(setOv).catch(() => {}); }, []);
  useEffect(() => { loadOv(); }, [loadOv]);
  const refresh = () => { load(); loadOv(); };

  const cols = [
    {
      key: "user", label: "User", render: (u) => (
        <span className="mx-user">
          <Avatar name={u.email || ""} />
          <Cell main={u.email} sub={u.name || null} />
        </span>
      ),
    },
    { key: "merchant", label: "Merchant", render: (u) => <Cell main={u.merchant?.name || null} sub={u.merchant?.kyc_status ? `KYC ${u.merchant.kyc_status}` : null} /> },
    { key: "role", label: "Role", hideSm: true, render: (u) => <Pill tone={u.role === "superadmin" ? "blue" : "gray"}>{u.role}</Pill> },
    { key: "status", label: "Status", render: (u) => <UserStatus u={u} /> },
    { key: "created", label: "Joined", hideSm: true, render: (u) => <Cell main={when(u.created_at)} sub={ago(u.created_at)} /> },
    {
      key: "actions", label: "Actions", align: "right", render: (u) => (
        <span className="mx-actions">
          {u.merchant && u.role !== "superadmin" && (
            <Button size="sm" variant={u.merchant.suspended ? "secondary" : "danger"} onClick={(e) => { e.stopPropagation(); setConfirm(u.merchant); }}>
              {u.merchant.suspended ? "Reactivate" : "Suspend"}
            </Button>
          )}
          {u.merchant && <Button size="sm" onClick={(e) => { e.stopPropagation(); go("merchants/" + u.merchant.id); }}>Manage</Button>}
        </span>
      ),
    },
  ];

  return (
    <Page>
      <Grid cols={3}>
        <Kpi hero icon={Users} label="Users" value={ov ? ov.users.toLocaleString("en-IN") : "—"} sub="all accounts" />
        <Kpi icon={Store} label="Merchants" value={ov ? ov.merchants.toLocaleString("en-IN") : "—"} sub="business accounts" />
        <Kpi icon={UserX} label="Suspended" value={ov ? ov.suspended_merchants.toLocaleString("en-IN") : "—"} sub={ov && !ov.suspended_merchants ? "none suspended" : "can't take payments"} />
      </Grid>

      <Onboard onCreated={refresh} />

      <Section title="Users" sub={`${(list.data.total || 0).toLocaleString("en-IN")} ${list.filters.q ? "matching" : "total"}`} icon={Users} pad={false}>
        <Toolbar>
          <SearchInput value={qInput} onChange={setQInput} placeholder="Search email or name…" testId="god-users-search" />
        </Toolbar>
        {list.error && <div className="mx-pad"><Callout tone="red" title="Couldn't load users">{list.error}</Callout></div>}
        <Table testId="god-users" columns={cols} rows={list.data.items} loading={list.loading}
          onRowClick={(u) => u.merchant && go("merchants/" + u.merchant.id)}
          empty={list.filters.q
            ? <Empty icon={Search} title="No users match">Try another email or name.</Empty>
            : <Empty icon={Users} title="No users yet">Onboard a merchant above to get started.</Empty>} />
        <Pagination page={list.page} pages={pagesOf(list.data, list.size)} total={list.data.total} size={list.size}
          onPage={list.setPage} onSize={(s) => { list.setSize(s); list.setPage(1); }} />
      </Section>

      {confirm && <SuspendDialog merchant={confirm} onClose={() => setConfirm(null)} onDone={() => { setConfirm(null); refresh(); }} />}
      <MerchantStyles />
    </Page>
  );
}

function UserStatus({ u }) {
  if (u.onboarding_pending) return <Pill tone="amber" dot>Pending setup</Pill>;
  if (!u.merchant) return <span className="k-faint">—</span>;
  return <StatusPill status={u.merchant.suspended ? "suspended" : "active"} />;
}

function Onboard({ onCreated }) {
  const [form, setForm] = useState({ email: "", business_name: "", vpa: "" });
  const [created, setCreated] = useState(null); // { email, setup_url, setup_link_sent }
  const [busy, setBusy] = useState(false);

  const emailNorm = form.email.trim().toLowerCase();
  // The users table only holds one page, so check for an existing account on the server.
  const emailLookup = useDebounced(emailNorm, 300);
  const [dupe, setDupe] = useState(false);
  useEffect(() => {
    let live = true;
    if (!emailLookup) { setDupe(false); return undefined; }
    god.users({ q: emailLookup, page: 1, page_size: 10 })
      .then((r) => { if (live) setDupe((r.items || []).some((u) => (u.email || "").toLowerCase() === emailLookup)); })
      .catch(() => { if (live) setDupe(false); });
    return () => { live = false; };
  }, [emailLookup]);
  const emailOk = EMAIL_RX.test(emailNorm);
  const vpaOk = VPA_RX.test(form.vpa.trim());
  const canSubmit = emailOk && vpaOk && form.business_name.trim() && !dupe && !busy;

  const create = async (e) => {
    e.preventDefault(); setCreated(null);
    // Guard client-side too, so the same merchant is never onboarded twice.
    if (dupe) { toast.error("That email is already onboarded — see the users list below."); return; }
    if (!emailOk) { toast.error("Enter a valid email address."); return; }
    if (!vpaOk) { toast.error("Enter a valid UPI VPA (name@bank)."); return; }
    if (!form.business_name.trim()) { toast.error("Business name is required."); return; }
    setBusy(true);
    try {
      const r = await god.createUser({ ...form, email: emailNorm, vpa: form.vpa.trim().toLowerCase() });
      setCreated({ email: emailNorm, setup_url: r.setup_url, setup_link_sent: r.setup_link_sent });
      setForm({ email: "", business_name: "", vpa: "" });
      toast.success(`Created ${emailNorm}`);
      onCreated();
    } catch (x) { toast.error(x.message); }
    finally { setBusy(false); }
  };

  return (
    <Section title="Onboard merchant" sub="Creates the account and a one-time setup link for the merchant." icon={UserPlus}>
      <form onSubmit={create} className="mx-onboard">
        <div className="k-form k-form-3">
          <Field label="Email">
            <Input type="email" placeholder="owner@business.com" value={form.email} autoComplete="off"
              style={dupe ? { borderColor: "#d93025" } : undefined} aria-invalid={dupe || undefined}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Business name">
            <Input placeholder="Acme Stores" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
          </Field>
          <Field label="UPI VPA">
            <Input mono placeholder="name@bank" value={form.vpa} onChange={(e) => setForm({ ...form, vpa: e.target.value })} />
          </Field>
        </div>
        {dupe && <Callout tone="red" title="Already onboarded">This email already has an account — it can't be onboarded again. Find it in the users list below.</Callout>}
        <div className="k-actions">
          <button type="submit" className="k-btn k-btn-primary" disabled={!canSubmit}><Plus size={16} strokeWidth={2.1} />{busy ? "Creating…" : "Create + setup link"}</button>
        </div>
      </form>
      {created && (
        <div style={{ marginTop: 14 }}>
          <Callout tone="green" title={`Created ${created.email}`}>
            <div className="mx-created">
              <span>{created.setup_link_sent
                ? "Setup link emailed. Also share it directly (email may not deliver in SES sandbox):"
                : "Email not sent — share this one-time setup link with the merchant:"}</span>
              <span className="mx-link">
                <Input mono readOnly value={created.setup_url || ""} onFocus={(e) => e.target.select()} aria-label="Setup link" />
                <CopyBtn value={created.setup_url} label="Setup link copied" />
              </span>
              <small>Link is single-use and expires in 3 days (or when they set their password).</small>
            </div>
          </Callout>
        </div>
      )}
    </Section>
  );
}

function SuspendDialog({ merchant, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const suspend = !merchant.suspended;
  const run = async () => {
    setBusy(true);
    try {
      if (suspend) await god.suspend(merchant.id); else await god.reactivate(merchant.id);
      toast.success(suspend ? `${merchant.name || "Merchant"} suspended` : `${merchant.name || "Merchant"} reactivated`);
      onDone();
    } catch (x) { toast.error(x.message); setBusy(false); }
  };
  return (
    <Dialog title={suspend ? "Suspend merchant?" : "Reactivate merchant?"} onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant={suspend ? "danger" : "primary"} onClick={run} disabled={busy}>{busy ? "Working…" : suspend ? "Suspend" : "Reactivate"}</Button>
      </>}>
      <p className="mx-p">
        {suspend
          ? <><b>{merchant.name || "This merchant"}</b> stops taking payments until reactivated.</>
          : <><b>{merchant.name || "This merchant"}</b> can take payments again.</>}
      </p>
    </Dialog>
  );
}

// ─────────────────────────── detail ───────────────────────────
function MerchantDetail({ id }) {
  const { go } = useNav();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [upiForm, setUpiForm] = useState(null); // { mode: "add"|"edit", row? }
  const [delUpi, setDelUpi] = useState(null);

  const load = useCallback(async () => {
    try { setD(await god.merchant(id)); setErr(""); }
    catch (e) { setErr(e.message); toast.error(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // UPI IDs are a short per-merchant list (paged in the browser); transactions are server-paged.
  const { slice: upiPage, pager: upiPager } = useClientPage(d?.upi_ids, 10);
  const txList = usePagedList((p) => god.merchantTransactions(id, p), {}, 10);

  const back = <Button variant="ghost" icon={ArrowLeft} onClick={() => go("merchants")}>All merchants</Button>;

  if (!d) {
    return (
      <Page>
        <div>{back}</div>
        {err ? <Callout tone="red" title="Couldn't load this merchant">{err}</Callout> : <Section><Skeleton rows={6} /></Section>}
      </Page>
    );
  }

  const m = d.merchant;
  const upis = d.upi_ids || [];
  const activeUpis = upis.filter((u) => u.active !== false).length;
  const todayCount = upis.reduce((a, u) => a + (u.today_count || 0), 0);

  const saveField = async (field, value) => {
    try { await god.updateMerchant(id, { [field]: value }); toast.success("Saved"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const toggleUpi = async (u) => {
    try { await god.updateUpi(id, u.id, { active: u.active === false }); toast.success(u.active === false ? `${u.vpa} turned on` : `${u.vpa} turned off`); load(); }
    catch (e) { toast.error(e.message); }
  };

  const upiCols = [
    {
      key: "vpa", label: "VPA", render: (u) => (
        <span className="mx-vpa">
          <Cell main={u.vpa} sub={u.label} mono />
          {u.is_default && <Pill tone="blue">default</Pill>}
        </span>
      ),
    },
    { key: "pri", label: "Priority", align: "right", hideSm: true, render: (u) => u.priority ?? <span className="k-faint">—</span> },
    { key: "cap", label: "Cap / day", align: "right", render: (u) => u.max_txns || <span className="k-faint">No cap</span> },
    { key: "today", label: "Today", align: "right", render: (u) => <b>{u.today_count}</b> },
    { key: "active", label: "Active", render: (u) => <Toggle on={u.active !== false} onChange={() => toggleUpi(u)} label={`Turn ${u.vpa} ${u.active === false ? "on" : "off"}`} /> },
    {
      key: "actions", label: "Actions", align: "right", render: (u) => (
        <span className="mx-actions">
          <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setUpiForm({ mode: "edit", row: u })} aria-label={`Edit ${u.vpa}`}>Edit</Button>
          {!u.is_default && <Button size="sm" variant="danger" icon={Trash2} onClick={() => setDelUpi(u)} aria-label={`Remove ${u.vpa}`}>Remove</Button>}
        </span>
      ),
    },
  ];

  const txCols = [
    { key: "payer", label: "Payer", render: (t) => <Cell main={t.payer_vpa || null} mono /> },
    { key: "amount", label: "Amount", align: "right", render: (t) => <b>{inr(t.amount)}</b> },
    { key: "status", label: "Status", render: (t) => <span className="mx-status"><StatusPill status={t.status} />{t.confirmation_source && <span className="k-faint">{t.confirmation_source}</span>}</span> },
    { key: "at", label: "When", render: (t) => <Cell main={when(t.created_at)} sub={ago(t.created_at)} /> },
  ];
  // Fall back to the detail payload's latest 10 if the paged endpoint fails.
  const txFallback = txList.error && (d.recent_transactions || []).length > 0;

  return (
    <Page>
      <div>{back}</div>

      <Section>
        <div className="mx-hero">
          <Avatar name={m.name || m.owner_email || ""} size={52} />
          <div className="mx-hero-main">
            <div className="mx-hero-title">{m.name || "Unnamed merchant"}</div>
            <div className="mx-hero-meta">
              <StatusPill status={m.suspended ? "suspended" : "active"} />
              {m.kyc_status && <Pill tone={m.kyc_status === "verified" ? "green" : "gray"}>KYC {m.kyc_status}</Pill>}
              {m.owner_email && <span>{m.owner_email}</span>}
              {m.created_at && <span>Joined {when(m.created_at)}</span>}
            </div>
            <div className="mx-hero-ref"><span>{m.id}</span><CopyBtn value={m.id} label="Merchant ID copied" /></div>
          </div>
          <div className="mx-hero-act">
            <Button variant={m.suspended ? "primary" : "danger"} onClick={() => setSuspendOpen(true)}>{m.suspended ? "Reactivate" : "Suspend"}</Button>
          </div>
        </div>
      </Section>

      {m.suspended && <Callout tone="red" title="This merchant is suspended">They can't take payments until reactivated.</Callout>}

      <Grid cols={4}>
        <Kpi hero icon={Banknote} label="Lifetime volume" value={inrCompact(d.lifetime?.sum)} sub={inr(d.lifetime?.sum)} />
        <Kpi icon={Receipt} label="Payments" value={(d.lifetime?.count || 0).toLocaleString("en-IN")} sub="settled, all time" />
        <Kpi icon={Landmark} label="UPI IDs" value={upis.length} sub={`${activeUpis} active`} />
        <Kpi icon={Receipt} label="Payments today" value={todayCount.toLocaleString("en-IN")} sub="across all UPI IDs" />
      </Grid>

      <div className="k-split">
        <Section title="Redirect URLs" sub="Where customers land after paying. Saved when the field loses focus." icon={Settings2}>
          <div className="k-form">
            <Field label="Success URL">
              <Input mono key={`s-${m.success_url}`} defaultValue={m.success_url} placeholder="https://…"
                onBlur={(e) => e.target.value !== m.success_url && saveField("success_url", e.target.value)} />
            </Field>
            <Field label="Failure URL">
              <Input mono key={`f-${m.failure_url}`} defaultValue={m.failure_url} placeholder="https://…"
                onBlur={(e) => e.target.value !== m.failure_url && saveField("failure_url", e.target.value)} />
            </Field>
          </div>
        </Section>
        <Section title="Profile" icon={Store}>
          <Rows items={[
            ["Owner email", m.owner_email, { copy: true }],
            ["KYC status", m.kyc_status],
            ["Created", m.created_at ? when(m.created_at) : null],
            ["Merchant ID", shortId(m.id), { mono: true, copy: m.id }],
          ]} />
        </Section>
      </div>

      <Section title="UPI IDs" sub="Rotated by priority; capped IDs fall through to the next." icon={Landmark} pad={false}
        actions={<Button size="sm" variant="primary" icon={Plus} onClick={() => setUpiForm({ mode: "add" })}>Add UPI ID</Button>}>
        <Table testId="god-upi" columns={upiCols} rows={upiPage}
          empty={<Empty icon={Landmark} title="No UPI IDs">Add one so this merchant can take payments.</Empty>} />
        <Pagination {...upiPager} pages={Math.max(1, Math.ceil(upiPager.total / upiPager.size))} />
      </Section>

      <Section title="Transactions" sub={`${(txFallback ? d.recent_transactions.length : txList.data.total || 0).toLocaleString("en-IN")} ${txFallback ? "most recent" : "total"}`} icon={Receipt} pad={false}>
        {txList.error && (
          <div className="mx-pad">
            <Callout tone="amber" title={txFallback ? "Showing the latest 10 only" : "Couldn't load transactions"}>{txList.error}</Callout>
          </div>
        )}
        {txFallback ? (
          <Table columns={txCols} rows={d.recent_transactions} />
        ) : (
          <>
            <Table testId="god-merchant-tx" columns={txCols} rows={txList.data.items} loading={txList.loading}
              empty={<Empty icon={Receipt} title="No transactions yet">Payments show up here once this merchant starts collecting.</Empty>} />
            <Pagination page={txList.page} pages={pagesOf(txList.data, txList.size)} total={txList.data.total} size={txList.size}
              onPage={txList.setPage} onSize={(s) => { txList.setSize(s); txList.setPage(1); }} />
          </>
        )}
      </Section>

      {suspendOpen && <SuspendDialog merchant={m} onClose={() => setSuspendOpen(false)} onDone={() => { setSuspendOpen(false); load(); }} />}
      {upiForm && <UpiDialog merchantId={id} state={upiForm} onClose={() => setUpiForm(null)} onDone={() => { setUpiForm(null); load(); }} />}
      {delUpi && <DeleteUpiDialog merchantId={id} upi={delUpi} onClose={() => setDelUpi(null)} onDone={() => { setDelUpi(null); load(); }} />}
      <MerchantStyles />
    </Page>
  );
}

function UpiDialog({ merchantId, state, onClose, onDone }) {
  const edit = state.mode === "edit";
  const row = state.row || {};
  const [f, setF] = useState({
    label: row.label || "", vpa: row.vpa || "",
    priority: row.priority != null ? String(row.priority) : "", max_txns: row.max_txns ? String(row.max_txns) : "",
  });
  const [busy, setBusy] = useState(false);
  const vpaOk = edit || VPA_RX.test(f.vpa.trim());

  const save = async (e) => {
    e?.preventDefault();
    if (!vpaOk) { toast.error("Enter a valid UPI VPA (name@bank)."); return; }
    setBusy(true);
    try {
      const cap = parseInt(f.max_txns, 10);
      if (edit) {
        const b = { label: f.label, max_txns: cap > 0 ? cap : 0 }; // 0 clears the cap
        if (f.priority !== "") b.priority = parseInt(f.priority, 10);
        await god.updateUpi(merchantId, row.id, b);
        toast.success(`${row.vpa} updated`);
      } else {
        const b = { label: f.label, vpa: f.vpa };
        if (cap > 0) b.max_txns = cap;
        await god.addUpi(merchantId, b);
        toast.success(`${f.vpa.trim().toLowerCase()} added`);
      }
      onDone();
    } catch (x) { toast.error(x.message); setBusy(false); }
  };

  return (
    <Dialog title={edit ? "Edit UPI ID" : "Add UPI ID"} onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={busy || !vpaOk}>{busy ? "Saving…" : edit ? "Save" : "Add UPI"}</Button>
      </>}>
      <form onSubmit={save} className="k-form">
        <Field label="VPA" wide hint={edit ? "The VPA can't be changed. Remove it and add a new one instead." : null}>
          <Input mono placeholder="vpa@bank" value={f.vpa} disabled={edit} onChange={(e) => setF({ ...f, vpa: e.target.value })} autoFocus={!edit} />
        </Field>
        <Field label="Label">
          <Input placeholder="Main counter" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        </Field>
        <Field label="Cap / day" hint="Settled payments per day. Blank = no cap.">
          <Input inputMode="numeric" placeholder="No cap" value={f.max_txns} onChange={(e) => setF({ ...f, max_txns: digits(e.target.value) })} />
        </Field>
        {edit && (
          <Field label="Priority" hint="Lower is used first.">
            <Input inputMode="numeric" value={f.priority} onChange={(e) => setF({ ...f, priority: digits(e.target.value) })} />
          </Field>
        )}
        <button type="submit" hidden aria-hidden="true" />
      </form>
    </Dialog>
  );
}

function DeleteUpiDialog({ merchantId, upi, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await god.deleteUpi(merchantId, upi.id); toast.success(`${upi.vpa} removed`); onDone(); }
    catch (x) { toast.error(x.message); setBusy(false); }
  };
  return (
    <Dialog title="Remove UPI ID?" onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={run} disabled={busy}>{busy ? "Removing…" : "Remove"}</Button>
      </>}>
      <p className="mx-p"><span className="k-mono">{upi.vpa}</span> stops receiving payments for this merchant. This can't be undone.</p>
    </Dialog>
  );
}

function MerchantStyles() {
  return (
    <style>{`
      .mx-user { display: inline-flex; align-items: center; gap: 10px; min-width: 0; }
      .mx-actions { display: inline-flex; gap: 6px; justify-content: flex-end; flex-wrap: wrap; }
      .mx-pad { padding: 12px 18px 0; }
      .mx-p { margin: 0; font-size: 14px; line-height: 1.5; color: #374151; }
      .mx-onboard { display: flex; flex-direction: column; gap: 14px; }
      .mx-created { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
      .mx-created small { font-size: 12px; opacity: .8; }
      .mx-link { display: flex; align-items: center; gap: 6px; min-width: 0; }
      .mx-link .k-input { font-size: 12px; }
      .mx-vpa { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; min-width: 0; }
      .mx-status { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 12.5px; }
      .mx-hero { display: flex; align-items: flex-start; gap: 16px; }
      .mx-hero-main { flex: 1; min-width: 0; }
      .mx-hero-title { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; color: #111827; overflow-wrap: anywhere; }
      .mx-hero-meta { display: flex; align-items: center; gap: 10px; margin-top: 8px; font-size: 13px; color: #5b6170; flex-wrap: wrap; }
      .mx-hero-meta > span:not(.k-pill) { overflow-wrap: anywhere; }
      .mx-hero-ref { display: flex; align-items: center; gap: 6px; margin-top: 10px; font-family: "JetBrains Mono", monospace; font-size: 12.5px; color: #5b6170; min-width: 0; }
      .mx-hero-ref > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .mx-hero-act { flex: none; }
      @media (max-width: 760px) {
        .mx-hero { flex-wrap: wrap; }
        .mx-hero-act { width: 100%; }
        .mx-hero-act .k-btn { width: 100%; }
        .mx-pad { padding: 12px 14px 0; }
      }
    `}</style>
  );
}
